import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { locations } from './location';
import './MapWithSidebar.css';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsIcon from '@mui/icons-material/Directions';

const defaultCenter = { lat: 10.061288662447902, lng: -84.44147566457765 };

export default function MapWithSidebar() {
  const [selected, setSelected] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const containerStyle = { width: '100%', height: '100dvh' };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['marker'],
    id: 'google-map-script',
  });

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);

  const onLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onUnmount = useCallback(() => {
    markersRef.current.forEach(m => (m.map = null));
    markersRef.current = [];
    mapRef.current = null;
  }, []);

  const onListClick = (loc) => {
    setSelected(loc);
    if (mapRef.current) {
      mapRef.current.panTo(loc.position);
      mapRef.current.setZoom(15);
    }
    if (isMobile) setSidebarOpen(false);
  };

  const toggleSidebar = () => setSidebarOpen(v => !v);
  const closeSidebar  = () => setSidebarOpen(false);

  // Crear marcadores básicos (AdvancedMarkerElement)
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (!window.google || !window.google.maps?.marker) return;

    markersRef.current.forEach(m => (m.map = null));
    markersRef.current = [];

    const { AdvancedMarkerElement, PinElement } = window.google.maps.marker;

    locations.forEach((loc) => {
      const pin = new PinElement({
        background: '#EA6226',
        borderColor: '#FFFFFF',
        glyphColor: '#FFFFFF',
      });

      const marker = new AdvancedMarkerElement({
        map: mapRef.current,
        position: loc.position,
        title: loc.title,
        content: pin.element,
      });

      marker.addListener('click', () => {
        setSelected(loc);
        if (isMobile) setSidebarOpen(false);
        if (mapRef.current) {
          mapRef.current.panTo(loc.position);
          mapRef.current.setZoom(15);
        }
      });

      markersRef.current.push(marker);
    });

    return () => {
      markersRef.current.forEach(m => (m.map = null));
      markersRef.current = [];
    };
  }, [isLoaded, isMobile]);

  const mapOptions = isMobile
    ? {
        styles: mapStyles,
        gestureHandling: 'greedy',
        zoomControl: true,
        fullscreenControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        controlSize: 28,
      }
    : {
        styles: mapStyles,
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
      };

  const zoomIn = () => mapRef.current && mapRef.current.setZoom(mapRef.current.getZoom() + 1);
  const zoomOut = () => mapRef.current && mapRef.current.setZoom(mapRef.current.getZoom() - 1);
  const recenter = () => mapRef.current && mapRef.current.panTo(defaultCenter);
  const toggleMapType = () => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapRef.current.getMapTypeId() === 'satellite' ? 'roadmap' : 'satellite');
  };
  const locateMe = () => {
    if (!navigator.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const pos = { lat: coords.latitude, lng: coords.longitude };
        mapRef.current.panTo(pos);
        mapRef.current.setZoom(15);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  if (loadError) return <div>Hubo un problema cargando Google Maps.</div>;

  return (
    <div className="mws-container">
      <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Abrir ubicaciones">
        {sidebarOpen ? 'Cerrar' : 'Ubicaciones'}
      </button>

      {isMobile && sidebarOpen && <div className="mws-backdrop" onClick={closeSidebar} />}

      <aside className={`mws-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Nuestras Ubicaciones</h2>
        </div>
        <ul>
          {locations.map(loc => (
            <li
              key={loc.id}
              onClick={() => onListClick(loc)}
              className={selected?.id === loc.id ? 'active' : ''}
            >
              <div className="location-marker" />
              <div className="location-info">
                <strong>{loc.title}</strong>
                <span className="location-description">
                  {loc.description.substring(0, 56)}…
                </span>
              </div>
            </li>
          ))}
        </ul>
      </aside>

      {isLoaded && (
        <div className="map-box">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={defaultCenter}
            zoom={12}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            {/* ⬇️ InfoWindow ahora SIEMPRE se muestra (desktop y móvil) */}
            {selected && (
              <InfoWindow position={selected.position} onCloseClick={() => setSelected(null)}>
                <div className="info-window">
                  <h3>{selected.title}</h3>
                  <p>{selected.description}</p>
                  {selected.phone && (
                    <div className="info-contact">
                      <PhoneIcon fontSize="small" />
                      <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                    </div>
                  )}
                  {selected.waze && (
                    <div className="info-waze">
                      <a href={selected.waze} target="_blank" rel="noopener noreferrer" className="waze-link">
                        <DirectionsIcon fontSize="small" />
                        Abrir en Waze
                      </a>
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>

          <div className={`map-controls ${isMobile ? 'show' : ''}`}>
            <button onClick={zoomIn} aria-label="Acercar">＋</button>
            <button onClick={zoomOut} aria-label="Alejar">−</button>
            <button onClick={locateMe} aria-label="Mi ubicación">📍</button>
            <button onClick={recenter} aria-label="Recentrar">◎</button>
            <button onClick={toggleMapType} aria-label="Tipo de mapa">🗺️</button>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStyles = [
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];
